import { StanButton, StanInput, StanLink } from '@/components';
import IconPerson from '@/assets/icons/ic-profile.svg';
import IconLock from '@/assets/icons/ic-lock.svg';
import Logo from '@/assets/images/stan-logo-text.png';
import { useStanRouter } from '@/hooks/useStanRouter';
import { useAuthStore } from '@/stores';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoginFormState } from '@/features/auth/types';
import { loginSchema } from '@/features/auth/validations';
import { useLoginMutation } from '@/features/auth/queries';
import { LoginResponse } from '@/lib/api/auth/types';

export default function LoginPage() {
    const {
        register,
        handleSubmit,
        formState: { errors },
        setError,
        watch,
    } = useForm<LoginFormState>({
        resolver: zodResolver(loginSchema),
        defaultValues: {
            emailOrUsername: '',
            password: '',
        },
    });

    const { push } = useStanRouter();
    const lastAccountKey = useAuthStore(state => state.lastAccountKey);
    const accounts = useAuthStore(state => state.accounts);
    const switchAccount = useAuthStore(state => state.switchAccount);
    const isAuthenticated = useAuthStore(state => state.isAuthenticated);
    const currentAccountKey = useAuthStore(state => state.currentAccountKey);
    const setLoginApiData = useAuthStore(state => state.setLoginApiData);
    const { mutate: loginMutate, isPending: isLoading } = useLoginMutation();
    const emailOrUsername = watch('emailOrUsername');
    const password = watch('password');

    const hasExistingAccount = useMemo(() => {
        if (lastAccountKey && accounts[lastAccountKey]) {
            return true;
        }
        return false;
    }, [lastAccountKey, accounts]);

    useEffect(() => {
        if (isAuthenticated && currentAccountKey) {
            push('/dashboard');
        }
    }, [isAuthenticated, push, currentAccountKey]);

    const backToLoggedInAccount = () => {
        if (hasExistingAccount && lastAccountKey) {
            switchAccount(lastAccountKey);
        }
        push('/dashboard');
    };

    const handleLogin = async (data: LoginFormState) => {
        loginMutate(
            { email_or_username: data.emailOrUsername, password: data.password },
            {
                onSuccess: (data: LoginResponse) => {
                    setLoginApiData(data);
                },
                onError: () => {
                    setError('password', { message: "We couldn't find that username/password combo in our system!" });
                },
            }
        );
    };

    return (
        <div className='w-full max-w-md space-y-8 p-8'>
            <div className='flex flex-col items-center gap-2'>
                <img className='mx-auto mb-6' src={Logo} height={100} width={100} alt='Stan Logo' />
                <p className='text-dark text-heading-heading2 text-center'>Get your bag 💰💸😤</p>
            </div>

            <div className='mt-8 space-y-6'>
                <form className='space-y-4' onSubmit={handleSubmit(handleLogin)}>
                    <StanInput
                        id='emailOrUsername'
                        type='text'
                        placeholder='Email or Username'
                        disabled={isLoading}
                        hintText={errors.emailOrUsername?.message}
                        error={!!errors.emailOrUsername}
                        startIcon={IconPerson}
                        value={emailOrUsername}
                        {...register('emailOrUsername')}
                    />

                    <StanInput
                        id='password'
                        type='password'
                        placeholder='Password'
                        autoComplete='current-password'
                        disabled={isLoading}
                        hintText={errors.password?.message}
                        error={!!errors.password}
                        startIcon={IconLock}
                        value={password}
                        {...register('password')}
                    />

                    <div className='mb-12 flex flex-col items-center justify-between gap-2'>
                        <StanButton
                            type='submit'
                            color='primary'
                            loading={isLoading}
                            disabled={isLoading}
                            className='w-full'
                            name='sign-in'
                        >
                            {isLoading ? 'Signing in...' : 'Sign in'}
                        </StanButton>

                        <div className='text-body-2-regular'>
                            <span
                                className='link-text'
                                onClick={e => {
                                    e.preventDefault();
                                    push('/forgot_password');
                                }}
                            >
                                Forgot your password?
                            </span>
                        </div>
                    </div>
                </form>
            </div>

            <div className='flex flex-col items-center justify-center'>
                <p className='text-body-2-bold text-dark'>
                    Don&apos;t have an account?{' '}
                    <StanLink to='/register' className='link-text'>
                        Sign Up
                    </StanLink>
                </p>
                {hasExistingAccount && (
                    <div className='w-full'>
                        <hr />
                        <div className='text-center'>
                            <p
                                className='text-brand-main text-body-2-bold cursor-pointer underline'
                                onClick={backToLoggedInAccount}
                            >
                                Go back to dashboard
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
